import React, { useEffect, useRef } from 'react';
import AnalyticsProvider from '@/components/common/AnalyticsProvider';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { loadGoogleAnalytics, removeGoogleAnalytics } from '@/lib/googleAnalytics';

interface ConsentAwareAnalyticsProps {
  children: React.ReactNode;
}

/**
 * Manages Google Analytics script injection based on cookie consent.
 * The script is only loaded when the user has granted analytics consent.
 * If consent is revoked, the script is removed from the DOM.
 * 
 * REQUIREMENTS:
 * - No tracking without consent
 * - No cookies set before acceptance
 * - Script loaded dynamically (not in index.html)
 */
export default function ConsentAwareAnalytics({ children }: ConsentAwareAnalyticsProps) {
  const { consent, hasConsented, canUseAnalytics } = useCookieConsent();
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    // Check if consent has been determined AND analytics is explicitly allowed
    // Only load script if BOTH conditions are true:
    // 1. hasConsented === true (consent has been determined)
    // 2. consent.analytics === true (user explicitly granted analytics consent)
    const shouldLoadScript = hasConsented && consent.analytics === true;

    if (shouldLoadScript && !scriptLoadedRef.current) {
      loadGoogleAnalytics();
      scriptLoadedRef.current = true;
    } else if (!shouldLoadScript && scriptLoadedRef.current) {
      removeGoogleAnalytics();
      scriptLoadedRef.current = false;
    } else if (!shouldLoadScript) {
      // Consent not yet determined or analytics not allowed - ensure script is not loaded
      removeGoogleAnalytics();
      scriptLoadedRef.current = false;
    }
  }, [consent.analytics, hasConsented]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Only remove if component unmounts and consent was revoked
      if (!canUseAnalytics()) {
        removeGoogleAnalytics();
        scriptLoadedRef.current = false;
      }
    };
  }, [canUseAnalytics]);

  // Only render AnalyticsProvider if consent is granted
  // This ensures page view tracking only happens after consent
  if (!canUseAnalytics()) {
    return <>{children}</>;
  }

  return <AnalyticsProvider enabled>{children}</AnalyticsProvider>;
}

